import React, { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import { FieldArray } from "formik";
import CostCenterModal from "./CostCenterModal";
import useQueryParams from "src/hooks/useQueryParams";
import sortBy from "lodash/sortBy";

const INITIAL_DISPLAY_COUNT = 10;

export default function CostCenters({ costCenters = [], readOnly = false }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  const costCenterId = queryParams.get("costCenterId");

  const sortedCostCenters = sortBy(costCenters, (costCenter) =>
    (costCenter.costCenterName || "").toLowerCase()
  );

  const visibleCostCenters = useMemo(() => {
    if (showDisabled) return sortedCostCenters;
    return sortedCostCenters.filter((costCenter) => !costCenter.disabled);
  }, [sortedCostCenters, showDisabled]);

  const filteredCostCenters = useMemo(() => {
    if (!searchQuery.trim()) return visibleCostCenters;

    const query = searchQuery.toLowerCase();
    return visibleCostCenters.filter((costCenter, index) => {
      const name = costCenter.costCenterName || `Cost Center ${index + 1}`;
      return (
        name.toLowerCase().includes(query) ||
        costCenter.costCenterCode?.toLowerCase().includes(query) ||
        costCenter.comments?.toLowerCase().includes(query)
      );
    });
  }, [visibleCostCenters, searchQuery]);

  const displayedCostCenters = useMemo(() => {
    if (showAll || searchQuery.trim()) return filteredCostCenters;
    return filteredCostCenters.slice(0, INITIAL_DISPLAY_COUNT);
  }, [filteredCostCenters, showAll, searchQuery]);

  const hasMore = filteredCostCenters.length > INITIAL_DISPLAY_COUNT;
  const hiddenCount = filteredCostCenters.length - INITIAL_DISPLAY_COUNT;

  const isNew = costCenterId === "new";
  const currentCostCenter = useMemo(() => {
    if (!costCenterId) return null;
    if (isNew) return {};
    return costCenters.find((cc) => cc.id === costCenterId) || null;
  }, [costCenterId, isNew, costCenters]);

  const handleOpenAddModal = useCallback(() => {
    const params = new URLSearchParams(queryParams);
    params.set("costCenterId", "new");
    navigate({ search: params.toString() });
  }, [navigate, queryParams]);

  const handleOpenEditModal = useCallback(
    (costCenter) => {
      const params = new URLSearchParams(queryParams);
      params.set("costCenterId", costCenter.id);
      navigate({ search: params.toString() });
    },
    [navigate, queryParams]
  );

  const handleCloseModal = useCallback(() => {
    const params = new URLSearchParams(queryParams);
    params.delete("costCenterId");
    navigate({ search: params.toString() });
  }, [navigate, queryParams]);

  const handleOpenSettingsMenu = useCallback((event) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseSettingsMenu = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography fontWeight={400}>
          Cost Centers {visibleCostCenters.length > 0 && `(${visibleCostCenters.length})`}
        </Typography>
        <Tooltip title="Cost center visibility settings">
          <IconButton size="small" onClick={handleOpenSettingsMenu}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={handleCloseSettingsMenu}
      >
        <MenuItem
          onClick={() => setShowDisabled((prev) => !prev)}
          sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
        >
          Show disabled cost centers
          <Switch size="small" checked={showDisabled} />
        </MenuItem>
      </Menu>

      {visibleCostCenters.length > INITIAL_DISPLAY_COUNT && (
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search cost centers"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {searchQuery && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              {filteredCostCenters.length}{" "}
              {filteredCostCenters.length === 1 ? "cost center" : "cost centers"} found
            </Typography>
          )}
        </Box>
      )}

      <FieldArray
        name="costCenters"
        render={(arrayHelpers) => (
          <Grid container spacing={1}>
            {displayedCostCenters.map((costCenter, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6 }}>
                <Box
                  data-id="cost-center-card"
                  py={1}
                  px={2}
                  border={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Box display="flex" flexDirection="column">
                    <Typography fontWeight={500}>
                      {costCenter.costCenterName || `Cost Center ${index + 1}`}
                    </Typography>
                    {costCenter.disabled && (
                      <Typography variant="caption" color="text.secondary">
                        Disabled
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {costCenter.costCenterCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {costCenter.comments}
                    </Typography>
                  </Box>
                  {!readOnly && (
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit Cost Center">
                        <IconButton
                          size="small"
                          aria-label="edit"
                          onClick={() => handleOpenEditModal(costCenter)}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}

            {!showAll && hasMore && !searchQuery && (
              <Grid size={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => setShowAll(true)}
                  sx={{ mt: 1 }}
                >
                  Show all {visibleCostCenters.length} cost centers ({hiddenCount} more)
                </Button>
              </Grid>
            )}

            <Grid size={12}>
              {!readOnly && (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddModal}>
                  Add {!!sortedCostCenters.length && "Another"} Cost Center
                </Button>
              )}
            </Grid>
          </Grid>
        )}
      />

      {!readOnly && currentCostCenter && (
        <CostCenterModal
          open
          onClose={handleCloseModal}
          costCenter={currentCostCenter}
          isNew={isNew}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}
